import { Capacitor } from '@capacitor/core';
import { Filesystem, Directory, Encoding } from '@capacitor/filesystem';
import localforage from 'localforage';

class StorageService {
  constructor() {
    this.isNativePlatform = Capacitor.isNativePlatform();
    
    if (!this.isNativePlatform) {
      localforage.config({
        name: 'SupercloudApp',
        storeName: 'app_storage',
        description: 'Local storage for Supercloud App'
      });
    }
    
    console.log(`📦 StorageService: Initialized for ${this.isNativePlatform ? 'Native' : 'Web'}`);
  }

  /**
   * Initialize and perform migrations
   */
  async initialize() {
    await this.migrateWebStorage();
  }

  /**
   * Migrate data from localStorage to localforage for Web platform
   * Focuses on visionBoards.json to preserve user images
   */
  async migrateWebStorage() {
    if (this.isNativePlatform) return;
    
    // We only want this migration to happen once, check non-blocking first
    if (this._migrationStarted) return;
    this._migrationStarted = true;
    
    try {
      const migratedFlag = await localforage.getItem('migration_completed_v1');
      
      // Even if migrated, we should clean up localStorage to free up space (especially for Native where we use Dexie/Filesystem)
      const keysToMigrate = [
        'visionBoards.json',
        'calorieai_food_records',
        'calorieai_goals',
        'calorieai_food_database',
        'calorieai_custom_foods',
        'calorieai_weight_records'
      ];
      
      if (migratedFlag) {
        // Already migrated, let's just make sure we aren't hoarding old data in localStorage
        for (const key of keysToMigrate) {
          if (localStorage.getItem(key)) {
            console.log(`🧹 Cleaning up obsolete legacy data from localStorage: ${key}`);
            localStorage.removeItem(key);
          }
        }
        return;
      }
      
      console.log('🔄 Starting web storage migration from localStorage to localforage...');
      
      let migrationCount = 0;
      
      for (const key of keysToMigrate) {
        const localData = localStorage.getItem(key);
        if (localData) {
          try {
            await localforage.setItem(key, localData);
            console.log(`✅ Migrated key: ${key}`);
            migrationCount++;
            // Clean up after successful migration to save memory
            localStorage.removeItem(key);
          } catch (e) {
            console.error(`❌ Failed to migrate key: ${key}`, e);
          }
        }
      }
      
      await localforage.setItem('migration_completed_v1', true);
      console.log(`🎉 Web storage migration completed! Migrated ${migrationCount} keys.`);
      
    } catch (error) {
      console.error('❌ Migration failed:', error);
    }
  }

  /**
   * Read data from storage
   * @param {string} key 
   * @param {any} defaultValue 
   * @returns 
   */
  async getItem(key, defaultValue = null) {
    try {
      if (this.isNativePlatform) {
        try {
          const file = await Filesystem.readFile({
            path: `${key.endsWith('.json') ? key : key + '.json'}`,
            directory: Directory.Data,
            encoding: Encoding.UTF8
          });
          // Capacitor returns string data which we need to return directly since caller parses it
          return file.data;
        } catch (fileError) {
          // files that do not exist yet should not crash the app, return null/defaultValue
          if (fileError.message && (fileError.message.includes('does not exist') || fileError.message.includes('File does not exist'))) {
            return defaultValue;
          }
          throw fileError;
        }
      } else {
        const data = await localforage.getItem(key);
        return data !== null ? data : defaultValue;
      }
    } catch (error) {
      console.error(`❌ StorageService getItem failed for ${key}:`, error);
      return defaultValue;
    }
  }

  /**
   * Save data to storage
   * @param {string} key 
   * @param {string} data - Stringified JSON data
   */
  async setItem(key, data) {
    try {
      if (this.isNativePlatform) {
        // Just write the file, Directory.Data exists by default on Android
        await Filesystem.writeFile({
          path: `${key.endsWith('.json') ? key : key + '.json'}`,
          data: data,
          directory: Directory.Data,
          encoding: Encoding.UTF8
        });
      } else {
        await localforage.setItem(key, data);
      }
      return true;
    } catch (error) {
      console.error(`❌ StorageService setItem failed for ${key}:`, error);
      throw error; // Throw error so callers can catch it and not hang
    }
  }

  /**
   * Delete item from storage
   * @param {string} key 
   */
  async removeItem(key) {
    try {
      if (this.isNativePlatform) {
        await Filesystem.deleteFile({
          path: `${key.endsWith('.json') ? key : key + '.json'}`,
          directory: Directory.Data
        });
      } else {
        await localforage.removeItem(key);
      }
      return true;
    } catch (error) {
      console.error(`❌ StorageService removeItem failed for ${key}:`, error);
      return false;
    }
  }
}

export const storageService = new StorageService();
export default storageService;
